import { IAnalyticsProvider } from "@next-platform/client-common-core";
import ReactGA from "react-ga4";

type QueuedEvent = {
  eventName: string;
  eventData: object;
};
export class AnalyticsProvider implements IAnalyticsProvider {
  private measurementId: string;
  private initialized: boolean = false;
  private static instance: AnalyticsProvider;
  private static queue: QueuedEvent[] = [];

  private constructor(measurementId: string) {
    this.measurementId = measurementId;
    this.initialize();
  }

  static getInstance(measurementId?: string): AnalyticsProvider | null {
    if (!AnalyticsProvider.instance) {
      if (!measurementId) {
        return null;
      }
      AnalyticsProvider.instance = new AnalyticsProvider(measurementId);
    }
    return AnalyticsProvider.instance;
  }
  static isInitialized(): boolean {
    return !!AnalyticsProvider.instance;
  }
  static queueEvent(eventName: string, eventData: object): void {
    AnalyticsProvider.queue.push({ eventName, eventData });
  }

  initialize() {
    if (this.initialized) return;
    ReactGA.initialize(this.measurementId);
    this.initialized = true;
    this.flushQueue();
  }

  private flushQueue() {
    while (AnalyticsProvider.queue.length > 0) {
      const { eventName, eventData } = AnalyticsProvider.queue.shift()!;
      ReactGA.event(eventName, eventData);
    }
  }

  async setUserProperties(properties: Record<string, string>): Promise<void> {
    if (!this.initialized) return;

    const { user_id, ...customUserProperties } = properties;

    if (user_id !== undefined) {
      if (user_id.trim() === "") {
        ReactGA.set({ user_id: null });
      } else {
        ReactGA.set({ user_id });
      }
    }

    const processedProperties: Record<string, string | null> = {};

    for (const [key, value] of Object.entries(customUserProperties)) {
      if (typeof value !== "string") continue;
      processedProperties[key] = value.trim() === "" ? null : value;
    }

    if (Object.keys(processedProperties).length > 0) {
      ReactGA.gtag("set", "user_properties", processedProperties);
    }
  }

  logEvent(eventName: string, eventData: object): void {
    if (!eventName) {
      return;
    }

    if (!this.initialized) {
      AnalyticsProvider.queue.push({ eventName, eventData });
      return;
    }
    ReactGA.event(eventName, {
      ...eventData,
    });
  }
}

export default AnalyticsProvider;
